import type { PaigeClient } from '@paige/api-client';
import { tenantHeader, unwrap } from './http';
import type { CompanyPartner, PartnerDiscount, StaffPartnerBilling } from './types';

/**
 * The company's partner (docs/partners.md): read it; its admins add, change or remove it at any
 * time; billing staff do the same with a reason.
 */
export interface PartnerApi {
  get(tenant: string): Promise<CompanyPartner>;
  /** Adds or changes it; a new code is checked by the API first (admin). */
  set(tenant: string, code: string): Promise<CompanyPartner>;
  /** Removes it: payments from now on credit nobody (admin). */
  remove(tenant: string): Promise<CompanyPartner>;
  /** The customer discount a code brings (ADR-0033), signed in or not; 0 for none. */
  discount(code: string): Promise<PartnerDiscount>;
  /** Billing staff add or change a company's partner, with the reason on the audit row. */
  attach(tenantId: string, code: string, reason: string): Promise<CompanyPartner>;
  /** Billing staff remove it, with the reason on the audit row. */
  detach(tenantId: string, reason: string): Promise<CompanyPartner>;
  /** Whether a partner pays for the company (ADR-0029); null when it pays Ademero itself. */
  billing(tenantId: string): Promise<StaffPartnerBilling | null>;
  /** Billing staff make the company's partner pay for it, with a reason. */
  setBilling(tenantId: string, ask: StaffPartnerBillingAsk): Promise<StaffPartnerBilling>;
  /** Billing staff end it now, with a reason: Free and a direct customer again. */
  unsetBilling(tenantId: string, reason: string): Promise<null>;
}

/** What staff send to make a partner pay for a company. */
export interface StaffPartnerBillingAsk {
  zohoAccountId: string;
  package: NonNullable<StaffPartnerBilling['package']>;
  interval: 'month' | 'year';
  /** With Enterprise: the quote recorded for the company (ADR-0036). */
  quoteId?: string | null;
  reason: string;
}

export function partnerApi(client: PaigeClient): PartnerApi {
  const h = (tenant: string) => ({ params: { header: tenantHeader(tenant) } });
  return {
    ...partnerBillingApi(client),
    async get(tenant) {
      return unwrap(await client.GET('/company/partner', h(tenant)));
    },
    async set(tenant, code) {
      return unwrap(await client.PUT('/company/partner', { ...h(tenant), body: { code } }));
    },
    async remove(tenant) {
      return unwrap(await client.DELETE('/company/partner', h(tenant)));
    },
    async discount(code) {
      return unwrap(await client.GET('/partner-discount', { params: { query: { code } } }));
    },
    async attach(tenantId, code, reason) {
      return unwrap(
        await client.PUT('/admin/tenants/{tenantId}/partner', {
          params: { path: { tenantId } },
          body: { code, reason },
        }),
      );
    },
    async detach(tenantId, reason) {
      return unwrap(
        await client.DELETE('/admin/tenants/{tenantId}/partner', {
          params: { path: { tenantId }, query: { reason } },
        }),
      );
    },
  };
}

/** Partner billing on the admin Customer page (ADR-0029): read, set and end it, with a reason. */
function partnerBillingApi(
  client: PaigeClient,
): Pick<PartnerApi, 'billing' | 'setBilling' | 'unsetBilling'> {
  return {
    async billing(tenantId) {
      return unwrap(
        await client.GET('/admin/tenants/{tenantId}/partner-billing', {
          params: { path: { tenantId } },
        }),
      ) as StaffPartnerBilling | null;
    },
    async setBilling(tenantId, ask) {
      return unwrap(
        await client.PUT('/admin/tenants/{tenantId}/partner-billing', {
          params: { path: { tenantId } },
          body: ask,
        }),
      ) as StaffPartnerBilling;
    },
    async unsetBilling(tenantId, reason) {
      unwrap(
        await client.DELETE('/admin/tenants/{tenantId}/partner-billing', {
          params: { path: { tenantId }, query: { reason } },
        }),
      );
      return null;
    },
  };
}
